import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompactMoney } from '@/lib/money';
import { financeColor } from '../lib/financeColors';
import { FinanceChartFilters, type FinanceChartFilterProps } from './FinanceChartFilters';
import { FinanceRefreshing } from './FinanceNotReady';

export function RevenueSourceTrendsCard({
  periods,
  series,
  currency,
  range,
  onRangeChange,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  startDate,
  endDate,
  isLoading = false,
}: {
  periods: string[];
  series: { name: string; category?: string; values: number[]; total: number }[];
  currency: string;
} & FinanceChartFilterProps) {
  const isDark = useIsDarkMode();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const visibleSeries = series.filter((item) => !hidden.has(item.name));
  const compactMoney = (value: number | string) => formatCompactMoney(Number(value), currency);
  const axisMoney = (value: number | string) =>
    formatCompactMoney(Number(value), currency, { withCode: false });

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      axis: tone(CHART_INK.axis, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        valueFormatter: compactMoney,
      },
      grid: { left: 6, right: 10, top: 12, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: periods,
        axisLine: { lineStyle: { color: ink.axis } },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10, formatter: axisMoney },
        splitLine: { lineStyle: { color: ink.grid, type: 'dashed' } },
      },
      series: visibleSeries.map((item) => {
        const color = financeColor(item.category ?? item.name);
        return {
          name: item.name,
          type: 'line',
          smooth: 0.2,
          showSymbol: false,
          lineStyle: { width: 2, color },
          itemStyle: { color },
          emphasis: { focus: 'series' },
          data: item.values,
        };
      }),
    };
  }, [currency, isDark, periods, visibleSeries]);

  function setSeriesVisible(name: string, visible: boolean) {
    setHidden((current) => {
      const next = new Set(current);
      if (visible) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-1 p-5 pb-1">
        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-sky-400">Revenue Source Trends · {currency}</p>
            <p className="text-[11px] text-muted-foreground">
              Gross Transaction Value by monetisation source over time
            </p>
          </div>
          <FinanceChartFilters
            range={range}
            onRangeChange={onRangeChange}
            countryCode={countryCode}
            onCountryCodeChange={onCountryCodeChange}
            availableCountryCodes={availableCountryCodes}
            {...(startDate ? { startDate } : {})}
            {...(endDate ? { endDate } : {})}
            dateAriaLabel="Change Revenue Source Trends date range"
            countryAriaLabel="Change Revenue Source Trends creator country"
          />
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-2">
        <FinanceRefreshing isLoading={isLoading}>
          <div className="grid items-center gap-5 lg:grid-cols-[minmax(0,1fr)_190px]">
        {periods.length === 0 || series.length === 0 ? (
          <p className="text-sm text-muted-foreground">No revenue trend data for this period.</p>
        ) : (
          <>
            <ReactECharts option={option} notMerge style={{ height: 260, width: '100%' }} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {series.map((item) => {
                const visible = !hidden.has(item.name);
                const color = financeColor(item.category ?? item.name);
                return (
                  <label key={item.name} className="flex cursor-pointer items-center gap-2 text-xs">
                    <Checkbox
                      checked={visible}
                      onCheckedChange={(checked) => setSeriesVisible(item.name, checked === true)}
                      style={{
                        borderColor: color,
                        backgroundColor: visible ? color : 'transparent',
                      }}
                      aria-label={`Show ${item.name}`}
                    />
                    <span className="min-w-0 flex-1 truncate">{item.name}</span>
                    <span className="text-muted-foreground">
                      {formatCompactMoney(item.total, currency)}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}
          </div>
        </FinanceRefreshing>
      </CardContent>
    </Card>
  );
}
